import CallErrorMessage, { Severity } from 'app/components/call/CallErrors/CallErrorMessage';
import { useI18n } from 'app/components/localization';
import LessonBookingRole from 'app/constants/LessonBookingRole';
import { CallStage, useCallStateContext } from 'app/containers/call/CallStateContext';
import { useBookingState } from 'app/containers/rtc/BookingContext/BookingState';
import { BookingType } from 'app/models/rtc/booking/constants';
import { BlockReason, IMetadata } from 'app/services/rtc/sync/ActionStream';
import { Map, Set } from 'immutable';
import React, { FunctionComponent, useMemo } from 'react';
import { useNotesContext } from 'app/containers/notes/NotesContext';
import { Stream } from '@engoo/realtime-protocol/stream';
import notesDocumentSpec from 'app/models/rtc/notes/documentSpec';
import navigatorDocumentSpec from 'app/models/navigator/documentSpec';
import selectionDocumentSpec from 'app/models/rtc/selection/documentSpec';
import chatDocumentSpec from 'app/models/chat/documentSpec';
import { dummyDrawDocumentSpec } from 'app/models/rtc/draw/documentSpec';
import { IDocumentIndex } from 'app/services/rtc/constants';
import entitySyncDocumentSpec from 'app/models/entityStateSync/documentSpec';
import useFormatList, { ListPatternType } from 'app/utils/useFormatList';
import { useSharedSelectionContext } from 'app/components/call/SharedSelectionDocumentContext';
import { useChatContext } from 'app/components/chat/ChatDocumentContext';
import { useEntityStateSyncContext } from 'app/containers/rtc/EntityStateSyncContext';
import { useSyncNavigator } from 'app/containers/call/SyncNavigatorContext';
import { useDraw } from 'app/components/rtc/draw/DrawContext';

type BlockedStreams = Map<BlockReason, Set<Stream>>;

interface IDeps {
  blockedStreams: BlockedStreams;
}

const documentSpecs: IDocumentIndex = {
  ...notesDocumentSpec,
  ...navigatorDocumentSpec,
  ...selectionDocumentSpec,
  ...entitySyncDocumentSpec,
  ...chatDocumentSpec,
  ...dummyDrawDocumentSpec,
};

const BlockedStreamsErrorMessage: FunctionComponent<IDeps> = ({ blockedStreams }) => {
  const { t } = useI18n();
  const bookingState = useBookingState();
  const { stage } = useCallStateContext();
  const formatList = useFormatList();

  // If there are no blocked documents then there is no error to show.
  if (blockedStreams.isEmpty()) {
    return null;
  }

  // Teachers at the end of lessons are expected to have all documents blocked, so
  // do not show an error message in that event.
  if (
    bookingState?.bookingType === BookingType.Lesson &&
    bookingState.lessonRole === LessonBookingRole.Teacher &&
    stage === CallStage.PostLocked
  ) {
    return null;
  }

  const featureList = (streams: Set<Stream>) => formatList(
    streams.toList()
    .map((stream) => documentSpecs[stream]?.name(t) ?? stream),
    ListPatternType.Unit,
  );

  const transitionalLessonEndBlockedStreams = blockedStreams.get(BlockReason.TransitionalLessonEnd);
  const quotaExceededBlockedStreams = blockedStreams.get(BlockReason.QuotaExceeded);

  return (
    <>
      {transitionalLessonEndBlockedStreams && (
        <CallErrorMessage severity={Severity.Info}>
          {t('rtc.errors.call-locked', { featureList: featureList(transitionalLessonEndBlockedStreams) })}
        </CallErrorMessage>
      )}

      {quotaExceededBlockedStreams && (
        <CallErrorMessage severity={Severity.Error}>
          {t('rtc.errors.documents-blocked', { featureList: featureList(quotaExceededBlockedStreams) })}
        </CallErrorMessage>
      )}
    </>
  );
};

const BlockedStreamsErrorMessageInjectDependencies: FunctionComponent = () => {
  const { metadata: notesMeta } = useNotesContext();
  const { metadata: navigatorMeta } = useSyncNavigator();
  const { metadata: selectionMeta } = useSharedSelectionContext();
  const { metadata: entitySyncMeta } = useEntityStateSyncContext();
  const { metadata: chatMeta } = useChatContext();
  const { metadata: drawMeta } = useDraw();

  const blockedStreams = useMemo(() => Map<BlockReason, Set<Stream>>().withMutations(all => {
    const metas: Array<[Stream, IMetadata | undefined]> = [
      [Stream.Notes, notesMeta],
      [Stream.Navigator, navigatorMeta],
      [Stream.Selection, selectionMeta],
      [Stream.EntityState, entitySyncMeta],
      [Stream.Chat, chatMeta],
      [Stream.Draw, drawMeta],
    ];

    for (const [stream, meta] of metas) {
      for (const reason of meta?.blockedReasons ?? []) {
        all.update(reason, Set(), (streams) => streams.add(stream));
      }
    }

    return all;
  }), [notesMeta, navigatorMeta, selectionMeta, entitySyncMeta, chatMeta, drawMeta]);

  return (
    <BlockedStreamsErrorMessage
      blockedStreams={blockedStreams}
    />
  );
};

export default BlockedStreamsErrorMessageInjectDependencies;
